<template>
  <div class="main-collection critical-component-clear">
    <collection-hero
      :title="title"
      :description="description"
      :description-limit="descriptionLimit > 0 ? descriptionLimit : 140"
    />

    <div class="container main-collection__show-filter">
      <button
        v-if="productFilters.length > 0"
        class="button button--light"
        type="button"
        :aria-label="$string('collection.filters.filter_sort')"
        @click.prevent="toggleOverlay({
          component: 'product-filters',
          ignoreDismissed: true,
          namespace: 'productFilters'
        })"
      >
        <span
          class="main-collection__show-filter-text text-body-s"
          v-text="$string('collection.filters.filter_sort')"
        />

        <span class="main-collection__show-filter-icon">
          <icon-plus />
        </span>
      </button>

      <p
        v-if="filterBy"
        class="text-body-xs"
        v-text="$string(
          'collection.filter_items',
          {
            replace: { count: collectionData.length, count_total: productCount }
          })"
      />

      <p
        v-else
        class="text-body-xs"
        v-text="$string(
          'collection.sort_items',
          {
            replace: { count: productCount }
          })"
      />
    </div>

    <div class="main-collection__filters">
      <product-filters
        :product-filters="productFilters"
        :current-sort="sortBy"
        :sorting="filteredSorting"
        :all-products-count="productCount"
        :products-count="collectionData.length"
        :is-modal="false"
        :swatches="swatches"
        :collection-handle="collectionHandle"
        :active-filters="activeFilters"
        @handle-sort-by="handleSortBy"
        @clear-filters="clearAllFilters"
        @active-filters="updateActiveFilters"
        @fetch-products="fetchProducts"
      />
    </div>

    <div
      class="main-collection__products grid"
      :class="[
        {
          'loading': isLoading,
          'container': !isMobile,
        }
      ]"
    >
      <div
        v-for="(product, index) of getItemsWithContentCard(collectionData)"
        :key="`main-collection-${index}-${product.handle}`"
        class="col l4"
        :class="(index + 1) % 3 ? 'xs2' : 'xs4'"
      >
        <media-blocks
          v-if="product.length"
          :style="{ '--text-color': product[0].textColor }"
          class="collection-content-card"
          :blocks="product"
          autoplay-override
        />

        <product-card
          v-else
          class="main-collection__product"
          :index="index"
          :product="product"
          :lazy="lazyLoadImage(index)"
          :expand="isMobile ? false : true"
        />
      </div>
    </div>

    <div
      v-if="loadMore"
      class="container grid"
    >
      <div
        class="col l5-8 xs4 main-collection__footer"
      >
        <p
          class="
            text-body-xs
            text-body-s-desktop
            main-collection__footer-text
          "
          v-text="$string(
            'collection.viewed_of',
            {
              replace: { count: collectionData.length, count_total: productCount }
            })"
        />

        <btn
          :label="$string('collection.load_more')"
          modifiers="outlined"
          show-icon
          @click="handleLoadMore"
        />
      </div>
    </div>

    <overlay
      key="productFilters"
      block-class="product-filters-overlay"
      direction="up"
      namespace="productFilters"
      type="modal"
    >
      <template #body="{ isActive }">
        <product-filters
          v-if="getComponentVisible('product-filters')"
          v-cloak
          :product-filters="productFilters"
          :current-sort="sortBy"
          :sorting="filteredSorting"
          :all-products-count="productCount"
          :products-count="collectionData.length"
          is-modal
          :is-active="isActive"
          :swatches="swatches"
          :active-filters="activeFilters"
          :collection-handle="collectionHandle"
          @handle-sort-by="handleSortBy"
          @clear-filters="clearAllFilters"
          @active-filters="updateActiveFilters"
          @fetch-products="fetchProducts"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main Collection.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn.vue'
import CollectionHero from '~async/collection-hero/collection-hero'
import IconPlus from '~icons/misc/plus.svg'
import MediaBlocks from '~async/media-blocks/media-blocks'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~async/product-card/product-card'
import ProductFilters from '~async/product-filters/product-filters'

const placeholderProducts = Array(window.cnvs.collection.productsPerPage).fill(false)

export default {
  name: 'MainCollection',

  components: {
    CollectionHero,
    ProductFilters,
    ProductCard,
    Btn,
    IconPlus,
    Overlay,
    MediaBlocks,
  },

  props: {
    collectionHandle: {
      type: String,
      default: '',
    },
    defaultSorting: {
      type: String,
      default: 'title-ascending',
    },
    collectionSorting: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    descriptionLimit: {
      type: Number,
      default: 140,
    },
    swatches: {
      type: Object,
      default: () => ({}),
    },
    contentCard: {
      type: Array,
      default: () => [],
    },
    vipCustomer: {
      type: Boolean,
      default: false,
    },
    collection: {
      type: Object,
      default: () => ({
        pageInfo: {},
        products: [],
        filters: [],
        sectionId: '',
      }),
    },
    productsPerPage: {
      type: Number,
      default: 24,
    },
  },

  data() {
    return {
      activeFilters: [],
      collectionData: placeholderProducts,
      filterBy: false,
      isLoading: false,
      loadMore: false,
      page: 1,
      productCount: this.collection.all_products_count,
      productFilters: this.collection.filters,
      query: [],
      sortBy: this.defaultSorting,
      vipProductCount: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute filtered sorting.
     * @returns {Array}
     */
    filteredSorting() {
      const sortOptions = [
        this.$string('collection.filters.featured'),
        this.$string('collection.filters.price_low_to_high'),
        this.$string('collection.filters.price_high_to_low'),
      ]
      return this.collectionSorting.filter((option) => sortOptions.includes(option.name))
    },
  },

  watch: {

    /**
     * Loads more products when page changes.
     */
    page() {
      this.fetchProducts(true, this.query)
    },
  },

  mounted() {
    this.sortBy = this.defaultSorting
    this.fetchInitialProducts()
  },

  methods: {

    /**
     * Returns label for active filter.
     */
    getActiveFilterLabel(filter) {
      const split = filter?.split('=')[1]
      if (split?.includes('colour:')) {
        return split?.split(':')[1]
      }

      return split?.replace('-', ' ')
    },

    /**
     * Checks whether or not to lazy load product card image.
     */
    lazyLoadImage(index) {
      return index > 8
    },

    /**
     * Updates active filter so children all use this single point of truth.
     */
    updateActiveFilters(filters) {
      this.activeFilters = filters
      this.page = 1
    },

    /**
     * Checks to see if there are existing queries in the URL.
     */
    checkUrlParameters() {
      const urlParameters = window.location.search

      if (urlParameters) {
        const querySplit = urlParameters.toString().split('?')[1].split('&')
        const validParams = querySplit.find(param => param.includes('filter'))

        if (validParams) {
          const sortByExists = querySplit.filter(query => query.includes('sort_by='))
          this.query = querySplit.map(filter => filter.replaceAll('%20', ' ')).filter(query => !query.includes('sort_by='))

          if (sortByExists.length) {
            this.sortBy = sortByExists[0].split('=')[1]
          }

          this.filterBy = true
        }

      }
    },

    /**
     * Fetches products from JSON template.
     */
    async fetchProducts(loadMore, query = []) {
      this.query = query
      this.isLoading = true
      const allQueries = query.join('&')
      let queryParameters = query.length === 0 ? '' : `${allQueries}`

      /**
       * Needs to use the href as might have a locale.
       */
      const domain = window.location.href.split('collections')[0]

      const url = `${domain}collections/${this.collectionHandle}`

      if (queryParameters) {
        history.pushState({
          html: '',
        }, '', `${this.collectionHandle}?sort_by=${this.sortBy}&${queryParameters}`)
      } else if (this.sortBy !== this.defaultSorting) {
        history.pushState({
          html: '',
        }, '', `${this.collectionHandle}?sort_by=${this.sortBy}`)

      } else if (!queryParameters) {
        history.pushState({
          html: '',
        }, '', `${this.collectionHandle}`)
      }

      if (loadMore) {
        queryParameters += `&page=${this.page}`
      }

      const response = await fetch(`${url}?view=json&sort_by=${this.sortBy}&${queryParameters}`)
      const data = await response.json()
      const filteredProducts = this.filterVipProducts(data.products)

      this.productFilters = data.filters

      this.collectionData = loadMore
        ? this.collectionData.concat(filteredProducts)
        : filteredProducts

      this.productCount = data.all_products_count
      const visibleProductCount = this.collectionData.length + this.vipProductCount
      this.loadMore = visibleProductCount < this.productCount
      this.isLoading = false
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Check to see if there's a valid content card and loads relevant amount.
     */
    fetchInitialProducts() {
      if (this.filterBy || this.sortBy !== this.defaultSorting) {
        this.fetchProducts()
        return
      }

      this.collectionData = this.filterVipProducts(this.collection.products)

      const visibleProductCount = this.contentCard.length
        ? this.collectionData.length + 1
        : this.collectionData.length

      if (
        visibleProductCount < this.productCount &&
        (visibleProductCount + this.vipProductCount) >= this.productsPerPage
      ) {
        this.loadMore = true
        return
      }

      this.loadMore = false
    },

    /**
     * Update items array to include content blocks.
     */
    getItemsWithContentCard(products) {
      const newArray = [...products]
      const pageType = cnvs.page.type
      let contentCard = false

      if (this.contentCard.length) {
        contentCard = true
      }

      if (pageType.toString() !== 'collection' || this.filterBy || this.sortBy !== this.defaultSorting) {
        return newArray
      }

      if (contentCard) {
        newArray.splice(5, 0, this.contentCard)
      }

      return newArray
    },

    /**
     * Handle clear all filters.
     */
    clearAllFilters() {
      this.query = []
      this.filterBy = false
      this.sortBy = this.defaultSorting

      const url = `${cnvs.store.shopDomain}collections/${this.collectionHandle}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Handle load more.
     */
    handleLoadMore() {
      if (this.collection.pageInfo.hasNextPage) {
        this.loadMore = true
      }

      this.page += 1
    },

    /**
     * Set sort type.
     * @param {SortType} [sortType]
     */
    handleSortBy(sortType) {
      this.sortBy = sortType
    },

    /**
     * Filter products tagged with vip.
     * @param {Array} products - Product data.
     * @returns {Array}
     */
    filterVipProducts(products) {
      if (this.vipCustomer) {
        return products
      }

      const filteredProducts = products.filter((product) => {
        return !product.tags.includes('vip')
      })

      const vipProducts = products.length - filteredProducts.length

      this.vipProductCount += vipProducts

      return filteredProducts
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
</style>
